import { useEffect, useRef, useState, type RefObject } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact' | 'flexible';
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

let cachedConfig: Promise<{ siteKey: string; enabled: boolean }> | null = null;

// Exported for tests only.
export function __resetTurnstileConfigCache() {
  cachedConfig = null;
}

function fetchTurnstileConfig() {
  if (!cachedConfig) {
    cachedConfig = fetch('/api/config')
      .then((r) => (r.ok ? r.json() : { turnstileSiteKey: '', turnstileEnabled: false }))
      .then((d: { turnstileSiteKey?: string; turnstileEnabled?: boolean }) => ({
        siteKey: d.turnstileSiteKey ?? '',
        enabled: Boolean(d.turnstileEnabled && d.turnstileSiteKey),
      }))
      .catch(() => ({ siteKey: '', enabled: false }));
  }
  return cachedConfig;
}

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>(`script[src^="${TURNSTILE_SCRIPT}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      // If it already finished loading, window.turnstile would be set (handled above);
      // otherwise wait for this tag's load/error rather than injecting a duplicate.
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile_load_failed')));
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `${TURNSTILE_SCRIPT}?render=explicit`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile_load_failed'));
    document.head.appendChild(s);
  });
}

export interface UseTurnstile {
  /** The verification token, '' until the widget produces one. */
  token: string;
  /** True once /api/config resolved (success or failure). */
  configLoaded: boolean;
  /** True when Turnstile is configured and should gate submit. */
  enabled: boolean;
  /** Attach to the element that hosts the widget. */
  widgetRef: RefObject<HTMLDivElement | null>;
  /** Reset the widget and clear the token (e.g. after a failed submit). */
  reset: () => void;
}

export function useTurnstile(): UseTurnstile {
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState('');
  const [configLoaded, setConfigLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await fetchTurnstileConfig();
      if (cancelled) return;
      setConfigLoaded(true);
      if (!cfg.enabled) return;
      setEnabled(true);
      try {
        await loadTurnstileScript();
      } catch {
        return;
      }
      if (cancelled || !widgetRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(widgetRef.current, {
        sitekey: cfg.siteKey,
        callback: (t) => setToken(t),
        'error-callback': () => setToken(''),
        'expired-callback': () => setToken(''),
        theme: 'light',
        size: 'flexible',
      });
    })();
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  const reset = () => {
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current);
    }
    setToken('');
  };

  return { token, configLoaded, enabled, widgetRef, reset };
}
